// React 19 removed `defaultProps` support for function components: the object
// is ignored outright, so an omitted prop arrives as `undefined` instead of the
// declared default. App source is codemodded to default parameters, but two
// prebuilt internal bundles still rely on the old behaviour and cannot be:
//   - @agconnections/grow-ui        (81 function-component defaultProps)
//   - syngenta-digital-cropwise-react-ui-kit (16)
// Both ship the classic JSX transform (createElement, no jsx-runtime), so
// re-applying the defaults at createElement time restores them. Without this,
// grow-ui's Popup reads `containerClasses.includes(...)` on undefined and
// white-screens the whole app on first render.
//
// Aliased over the bare `react` specifier via vite.config.js / vitest.config.ts;
// `__react_actual__` is the alias pointing at the real package entry.
// Remove once both packages publish React 19-compatible builds.
// eslint-disable-next-line import/no-unresolved -- vite.config.js alias
import * as React from '__react_actual__';

const baseCreateElement = React.createElement;

export function applyLegacyDefaultProps(type, props) {
  // Host elements ('div') never had defaultProps.
  if (type == null || typeof type === 'string') return props;
  // Class components keep native defaultProps handling in React 19.
  if (typeof type === 'function' && type.prototype?.isReactComponent) {
    return props;
  }
  // Everything else that can carry defaultProps: plain function components,
  // and forwardRef/memo wrappers — which are objects, not functions, so an
  // earlier `typeof type !== 'function'` guard silently skipped them. grow-ui's
  // Input is a forwardRef whose defaults include `type: 'text'` and `unit: ''`;
  // without them it looks its mask config up under `undefined` and throws
  // "Cannot set properties of undefined (setting 'mask')".
  if (typeof type !== 'function' && typeof type !== 'object') return props;

  // memo() can carry the defaults on the wrapper or on the component it wraps.
  const defaults = type.defaultProps ?? type.type?.defaultProps;
  if (!defaults) return props;

  // React's original semantics: a default fills in for `undefined`, not for
  // null/false/0/''.
  const merged = { ...props };
  Object.keys(defaults).forEach(key => {
    if (merged[key] === undefined) merged[key] = defaults[key];
  });
  return merged;
}

export function createElement(type, props, ...children) {
  return baseCreateElement(
    type,
    applyLegacyDefaultProps(type, props),
    ...children
  );
}

// Enumerated rather than `export *`: react is CJS, and Vite's dev optimizer
// can't statically analyse a star re-export of CJS — it silently drops every
// named export, so the first `import { createContext } from 'react'` fails.
/* eslint-disable camelcase -- mirroring React's own export names verbatim */
export const {
  // react-dom resolves the shared-internals object through the `react`
  // specifier, which this shim now stands in front of — omitting these
  // breaks the renderer, not just the shim.
  __CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE,
  __COMPILER_RUNTIME,
  Activity,
  Children,
  Component,
  Fragment,
  Profiler,
  PureComponent,
  StrictMode,
  Suspense,
  act,
  cache,
  cacheSignal,
  captureOwnerStack,
  cloneElement,
  createContext,
  createRef,
  forwardRef,
  isValidElement,
  lazy,
  memo,
  startTransition,
  unstable_useCacheRefresh,
  use,
  useActionState,
  useCallback,
  useContext,
  useDebugValue,
  useDeferredValue,
  useEffect,
  useEffectEvent,
  useId,
  useImperativeHandle,
  useInsertionEffect,
  useLayoutEffect,
  useMemo,
  useOptimistic,
  useReducer,
  useRef,
  useState,
  useSyncExternalStore,
  useTransition,
  version
} = React;
/* eslint-enable camelcase */

export default { ...React, createElement };
